import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TextInput from '../../components/TextInput';
import TextAreaInput from '../../components/TextAreaInput';
import CheckboxInput from '../../components/CheckboxInput';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import serviceApi from '../../api/faqApi';
import { useToast } from '../../contexts/ToastContext';

const EditFAQ = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [generalError, setGeneralError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    is_active: 0, // Use is_active to match backend
  });
  const [faqFound, setFaqFound] = useState(true);

  useEffect(() => {
    const fetchFaq = async () => {
      setLoading(true);
      setGeneralError(null);
      setValidationErrors({});
      try {
        const response = await serviceApi.getFaqByIdentifier(id);
        if (response.success && response.data) {
          const faqToEdit = response.data;
          setFormData({
            question: faqToEdit.question,
            answer: faqToEdit.answer,
            is_active: faqToEdit.is_active ? 1 : 0, // Convert to 1 or 0
          });
          setFaqFound(true);
        } else {
          setFaqFound(false);
          setGeneralError(response.message || 'FAQ not found.');
        }
      } catch (err) {
        console.error('Failed to fetch FAQ:', err);
        setFaqFound(false);
        setGeneralError(err.response?.data?.message || 'Failed to load FAQ. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchFaq();
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value,
    });
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  // Removed handleAnswerChange as TextAreaInput uses standard onChange
  // const handleAnswerChange = (content) => {
  //   setFormData({ ...formData, answer: content });
  //   if (validationErrors.answer) {
  //     setValidationErrors(prev => ({ ...prev, answer: undefined }));
  //   }
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setGeneralError(null);
    setValidationErrors({});

    const data = new FormData();
    for (const key in formData) {
      data.append(key, formData[key]);
    }

    try {
      await serviceApi.updateFaq(id, data);
      addToast('FAQ updated successfully!', 'success');
      navigate('/faqs');
    } catch (err) {
      console.error('Failed to update FAQ:', err);
      if (err.response && err.response.status === 422) {
        setValidationErrors(err.response.data.errors || {});
        addToast(err.response.data.message || 'Validation failed. Please check your input.', 'error');
      } else {
        addToast(err.response?.data?.message || 'Failed to update FAQ. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white text-center">
        <SkeletonLoader type="form" />
      </div>
    );
  }

  if (!faqFound) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white text-center">
        <h1 className="text-3xl font-bold mb-4">FAQ Not Found</h1>
        <p className="text-text-gray">{generalError || 'The FAQ you are trying to edit does not exist.'}</p>
        <Button
          onClick={() => navigate('/faqs')}
          className="mt-4 bg-gray-500 hover:bg-gray-600 text-white"
        >
          Go to FAQs List
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white">
      <h1 className="text-3xl font-bold mb-6">Edit FAQ</h1>
      {generalError && <div className="text-red-500 mb-4">{generalError}</div>}
      <form onSubmit={handleSubmit}>
        <TextInput
          label="Question"
          name="question"
          id="question"
          value={formData.question}
          onChange={handleChange}
          required={true}
          placeholder="Question"
          error={validationErrors?.question?.[0]}
        />
        <TextAreaInput // Changed from CKEditorComponent
          label="Answer"
          name="answer"
          id="answer"
          value={formData.answer}
          onChange={handleChange} // Use standard handleChange
          required={true}
          rows={4}
          placeholder="Answer"
          error={validationErrors?.answer?.[0]}
        />
        <CheckboxInput
          label="Active"
          name="is_active" // Use is_active to match backend
          id="is_active"
          checked={formData.is_active}
          onChange={handleChange}
        />
        <div className="flex items-center justify-between mt-6">
          <Button
            type="submit"
            className="bg-accent-red hover:bg-red-700 text-white"
            loading={loading}
          >
            Update FAQ
          </Button>
          <Button
            type="button"
            onClick={() => navigate('/faqs')}
            className="bg-gray-500 hover:bg-gray-600 text-white"
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditFAQ;